<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { AdsIcon, AdsListOption } from '@accor/ads-components'
import { IconChevronDown } from '@accor/icons'
import { useI18n } from 'vue-i18n'
import { useSideNav } from '@/composables/useSideNav'
import { useNavigation } from '@/composables/useNavigation'
import type { MenuSection } from '@/types/navigation'

const props = defineProps<{
  menuSection: MenuSection
  isOpen: boolean
}>()

const emit = defineEmits<{
  toggle: [event: Event, label: string]
}>()

const { menuEntriesToListOptions } = useSideNav()
const { updateSelectedItem } = useNavigation()

const { t } = useI18n()

const label = computed(() => t(props.menuSection.i18nLabel))
const handleToggle = (event: Event) => emit('toggle', event, props.menuSection.i18nLabel)
const optionClicked = (index: number) => updateSelectedItem(props.menuSection.children[index])
const optionsList = computed(() => menuEntriesToListOptions(props.menuSection.children))

const listRef = useTemplateRef('list')
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen)
      nextTick(() => listRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }))
  }
)
</script>

<template>
  <details
    class="side-nav-group"
    :open="props.isOpen"
    data-testid="nav-category"
    @toggle.stop="handleToggle"
  >
    <summary class="side-nav-group__summary">
      <div class="side-nav-group__summary-content">
        <span class="side-nav-group__label">{{ label }}</span>
        <AdsIcon
          :icon="IconChevronDown"
          size="sm"
          aria-hidden="true"
          class="side-nav-group__chevron"
        />
      </div>
    </summary>

    <AdsListOption ref="list" :options="optionsList" @option-clicked="optionClicked" />
  </details>
</template>

<style lang="scss">
@use '@/styles/utils';

.side-nav-group {
  &__summary {
    @include ads-sass.body-default-02;
    display: flex;
    align-items: center;
    padding: 0 0 ads.$ads-spacing-inline-sm;
    width: 100%;
    color: inherit;
    user-select: none;
    cursor: pointer;
  }

  &__summary-content {
    display: flex;
    align-items: center;
    width: 100%;
  }

  &__label {
    flex: 1;
  }

  &__chevron {
    transition: transform 0.2s ease;
  }

  &[open] &__chevron {
    transform: rotate(180deg);
  }
}
</style>
